'use client';

import { menuItems } from '@/data/globals.data';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import ImageOptimizer from './image-optimizer';

export default function MobileSidebar({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(); // Set "블로그" open by default to match the image
  const sidebarRef = useRef<HTMLDivElement>(null);

  // 사이드바 열릴 때 body 스크롤 막기
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.scrollTo({ top: 0 });
      if (sidebarRef.current) {
        sidebarRef.current.scrollTop = 0;
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const toggleSubmenu = (title: string) => {
    if (openSubmenu === title) {
      setOpenSubmenu(null);
    } else {
      setOpenSubmenu(title);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        // top-14
        <motion.div
          className="absolute top-0 right-0 bottom-0 left-0 z-[9999]"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* 오버레이 */}
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />
          {/* 가운데 500px 컨텐츠 */}
          <div
            ref={sidebarRef}
            className="relative mx-auto flex h-screen w-full flex-col bg-white"
          >
            {/* Yellow banner at top */}
            {/* <div className="bg-main-yellow text-level-2 typo-16 flex h-14 items-center justify-center px-4 py-2 font-extrabold">
              🚀4월 30일 까지!&nbsp;
              <span className="text-[#ff0000]">45일 무제한</span>&nbsp;연습
              제공&nbsp;
              <ImageOptimizer
                src="/images/icons/icon_black_arrow_right.svg"
                alt="arrow right"
                width={16}
                height={16}
              />
            </div> */}

            {/* Header with logo and close button */}
            <div className="border-stroke-white flex h-20 items-center justify-between border-b px-6">
              <div className="flex items-center">
                <Link href="/" className="relative" onClick={onClose}>
                  <Image
                    src="/images/logo.webp"
                    alt="Logo"
                    width={186}
                    height={30}
                  />
                </Link>
              </div>
              <div className="flex items-center gap-2.5">
                <Link href="/franchise" onClick={onClose}>
                  <button className="bg-main-darkgray desktop:px-[22px] desktop:py-[11px] desktop:rounded-sm flex shrink-0 cursor-pointer rounded-lg px-4 py-3">
                    <span className="typo-16 desktop:typo-18 font-bold text-white">
                      가맹 문의
                    </span>
                  </button>
                </Link>
                <button onClick={onClose} className="p-1">
                  <X
                    className="text-level-2 size-8 cursor-pointer"
                    strokeWidth={1.5}
                  />
                </button>
              </div>
            </div>

            {/* Menu items */}
            <nav className="hide-scrollbar flex-1 overflow-y-auto pt-3">
              <ul>
                {menuItems.map((item) => (
                  <li key={item.label}>
                    {item.submenu ? (
                      <div>
                        <button
                          onClick={() => toggleSubmenu(item.label)}
                          className="typo-22 text-level-1 flex w-full items-center justify-between px-6 py-4 font-bold"
                        >
                          {item.label}
                          {openSubmenu === item.label ? (
                            <ChevronUp
                              className="text-level-3 size-8"
                              strokeWidth={1.5}
                            />
                          ) : (
                            <ChevronDown
                              className="text-level-3 size-8"
                              strokeWidth={1.5}
                            />
                          )}
                        </button>

                        <AnimatePresence>
                          {openSubmenu === item.label && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <ul className="bg-bg-graylight mx-6 rounded-xl py-3.5">
                                {item.submenu.map((subItem) => (
                                  <li key={subItem.label}>
                                    <Link
                                      href={subItem.href || ''}
                                      className="text-level-3 typo-20 block px-6 py-2.5 font-semibold"
                                      onClick={onClose}
                                    >
                                      {subItem.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        href={item.href || ''}
                        className="typo-22 text-level-1 block px-6 py-5 font-bold"
                        onClick={onClose}
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}

                <li className="pt-3">
                  <Link href="https://www.dztraining.co.kr" target="_blank">
                    <ImageOptimizer
                      alt="드라이빙존 운전연수 바로가기 버튼"
                      src="/images/dzt_bn.png"
                      width={327}
                      height={74}
                      className="aspect-[327/74] w-full object-contain px-6"
                    />
                  </Link>
                </li>
                <li>
                  <div className="h-50"></div>
                </li>
              </ul>
            </nav>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
